import { Box, Checkbox, Divider, FormControlLabel, FormGroup, Grid, styled, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../features/authSlice";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import theme from "../../theme";
import { Add, Close } from "@mui/icons-material";
import CommonButton from "../../common/components/CommonButton";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { adminResetPassword } from "../../api/authApi";
import CommonDialog from "../../common/components/CommonDialog";
import CommonFormField from "../../common/components/CommonFormField";
import { MentorStatus, removeEmptyFields, serviceOptions, validateEmail } from "../../common/const";
import { blockingMentor, deleteMentor, getMentor, getMentorList, inviteMentor, resendInvitation, unBlockingMentor, updateMentorData } from "../../api/mentorApi";

const Cell = styled(TableCell)(({ theme }) => ({
    fontWeight: 600,
    padding: 5,
    textAlign: 'center',
    fontSize: 13,
    borderRight: `1px solid ${theme.palette.background.secondary}`,
    background: theme.palette.secondary.main,
    color: theme.palette.primary.main
}));

const InnerCell = styled(TableCell)(({ theme }) => ({
    padding: 10,
    textAlign: 'center',
    fontSize: 14,
    color: theme.palette.background.dark,
    borderRight: `1px solid ${theme.palette.background.secondary}`,
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 1,
    height: 'auto',
    maxHeight: '600px',
    overflow: 'auto',
}));

const ManageMentor = () => {
    const dispatch = useDispatch();
    const { loading } = useSelector((state) => state.auth);
    const [mentors, setUsers] = useState(null);
    const [pageNo, setPageNo] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [editConfirm, setEditConfirm] = useState(null);
    const [resetDialog, setResetDialog] = useState(false);
    
    // Validation error states
    const [firstNameError, setFirstNameError] = useState('');
    const [lastNameError, setLastNameError] = useState('');
    
    const [formData, setFormData] = useState({
        password: '',
        confirmpassword: ''
    });
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [mentorId, setMentorId] = useState(null);
    const [mentorData, setMentorData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        service_offers: [],
    });
    const [addMentor, setAddMentor] = useState(false);

    // BUG - 1 & 2: Validate First Name and Last Name (must contain at least one letter)
    const validateName = (value, fieldName) => {
        if (value.trim() === '') {
            if (fieldName === 'first_name') setFirstNameError('');
            if (fieldName === 'last_name') setLastNameError('');
            return true;
        }
        
        // Check if the value contains only numbers (no letters)
        const onlyNumbersRegex = /^[0-9\s]+$/;
        if (onlyNumbersRegex.test(value)) {
            const errorMsg = `${fieldName === 'first_name' ? 'First name' : 'Last name'} cannot contain only numbers. Please add some text.`;
            if (fieldName === 'first_name') setFirstNameError(errorMsg);
            if (fieldName === 'last_name') setLastNameError(errorMsg);
            return false;
        }
        
        // Check if value has at least one letter
        const hasLetter = /[A-Za-z]/.test(value);
        if (!hasLetter) {
            const errorMsg = `${fieldName === 'first_name' ? 'First name' : 'Last name'} must contain at least one letter`;
            if (fieldName === 'first_name') setFirstNameError(errorMsg);
            if (fieldName === 'last_name') setLastNameError(errorMsg);
            return false;
        }
        
        // Allow only letters and spaces
        const validCharsRegex = /^[A-Za-z\s]+$/;
        if (!validCharsRegex.test(value)) {
            const errorMsg = `${fieldName === 'first_name' ? 'First name' : 'Last name'} should only contain letters and spaces`;
            if (fieldName === 'first_name') setFirstNameError(errorMsg);
            if (fieldName === 'last_name') setLastNameError(errorMsg);
            return false;
        }
        
        if (fieldName === 'first_name') setFirstNameError('');
        if (fieldName === 'last_name') setLastNameError('');
        return true;
    };

    const validateField = (name, value, allValues = formData) => {
        switch (name) {
            case 'password':
                if (!value) return 'Password is required';
                if (value.length < 8) return 'Password must be at least 8 characters';
                if (!/[A-Z]/.test(value)) return 'Password must contain at least one uppercase letter';
                if (!/[a-z]/.test(value)) return 'Password must contain at least one lowercase letter';
                if (!/\d/.test(value)) return 'Password must contain at least one number';
                if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) return 'Password must contain at least one special character';
                return '';
            case 'confirmpassword':
                if (!value) return 'Confirm password is required';
                if (value !== allValues.password) return 'Passwords do not match';
                return '';
            default:
                return '';
        }
    };

    const validateForm = () => {
        const newErrors = {};

        const passwordError = validateField('password', formData.password);
        if (passwordError) newErrors.password = passwordError;

        const confirmPasswordError = validateField('confirmpassword', formData.confirmpassword);
        if (confirmPasswordError) newErrors.confirmpassword = confirmPasswordError;

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        const error = validateField(name, value, { ...formData, [name]: value });

        if (error) {
            setErrors(prev => ({ ...prev, [name]: error }));
        } else {
            setErrors(prev => ({ ...prev, [name]: undefined }));
        }

        if (name === 'password' && formData.confirmpassword) {
            const confirmError = validateField('confirmpassword', formData.confirmpassword, { ...formData, [name]: value });
            if (confirmError) {
                setErrors(prev => ({ ...prev, confirmpassword: confirmError }));
            } else {
                setErrors(prev => ({ ...prev, confirmpassword: undefined }));
            }
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        const error = validateField(name, value);
        if (error) {
            setErrors(prev => ({ ...prev, [name]: error }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (!validateForm()) return;
            dispatch(setLoading({
                loading: true
            }));
            const response = await adminResetPassword({
                userId: mentorId,
                newPassword: formData.password,
                confirmPassword: formData.confirmpassword
            });
            if (response.success) {
                showNotification(response.message, 'success');
                dispatch(setLoading({
                    loading: false
                }));
                setShowPassword(false);
                setShowConfirmPassword(false);
                setFormData({
                    password: '',
                    confirmpassword: ''
                });
                setMentorId(null);
                setResetDialog(false);
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            dispatch(setLoading({
                loading: false
            }));
            showNotification(errorMessage, 'error');
        }
    };

    const getMentorData = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getMentorList(pageNo + 1, pageSize, '', '', false);
            setUsers(response.data);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    useEffect(() => {
        getMentorData();
    }, [pageNo, pageSize]);

    const handleChangePage = (event, newPage) => {
        setPageNo(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setPageSize(parseInt(event.target.value, 10));
        setPageNo(0);
    };

    const handleBlock = async (user) => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            let response;
            if (user?.status === 'INACTIVE') {
                response = await unBlockingMentor(user.user_id);
            } else {
                response = await blockingMentor(user.user_id);
            }
            if (response.success) {
                showNotification(response.message, 'success');
                dispatch(setLoading({
                    loading: false
                }));
                setDeleteConfirm(null);
                getMentorData();
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    // BUG - 5: Delete Mentor functionality
    const handleDeleteMentor = async (user) => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await deleteMentor(user.user_id);
            if (response.success) {
                showNotification(response.message, 'success');
                dispatch(setLoading({
                    loading: false
                }));
                setDeleteConfirm(null);
                getMentorData();
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            
            {/* Delete Confirmation Dialog for Mentor Deletion */}
            <CommonDialog
                open={deleteConfirm && deleteConfirm.action === 'delete'}
                size="xs"
                content={(
                    <center>
                        <Typography sx={{ fontWeight: 600, fontSize: '22px', mt: 0.5, mb: 1 }}>
                            Are you sure you want to permanently delete this mentor?
                        </Typography>
                        <Typography sx={{ fontSize: '14px', color: 'gray', mt: 1 }}>
                            This action cannot be undone.
                        </Typography>
                    </center>
                )}
                actionButtons={(
                    <Fragment>
                        <CommonButton
                            label="Yes"
                            size="small"
                            background={theme.palette.secondary.main}
                            color={theme.palette.primary.main}
                            handleClick={async () => {
                                handleDeleteMentor(deleteConfirm)
                            }}
                        />
                        <CommonButton
                            label="No"
                            size="small"
                            background={theme.palette.background.main}
                            border={`1px solid ${theme.palette.background.light}`}
                            handleClick={() => {
                                setDeleteConfirm(null)
                            }}
                        />
                    </Fragment>
                )}
            />

            {/* Block Confirmation Dialog */}
            <CommonDialog
                open={deleteConfirm && deleteConfirm.action === 'block'}
                size="xs"
                content={(
                    <center>
                        <Typography sx={{ fontWeight: 600, fontSize: '22px', mt: 0.5, mb: 1 }}>
                            Are you sure you want to block this mentor?
                        </Typography>
                    </center>
                )}
                actionButtons={(
                    <Fragment>
                        <CommonButton
                            label="Yes"
                            size="small"
                            background={theme.palette.secondary.main}
                            color={theme.palette.primary.main}
                            handleClick={async () => {
                                handleBlock(deleteConfirm)
                            }}
                        />
                        <CommonButton
                            label="No"
                            size="small"
                            background={theme.palette.background.main}
                            border={`1px solid ${theme.palette.background.light}`}
                            handleClick={() => {
                                setDeleteConfirm(null)
                            }}
                        />
                    </Fragment>
                )}
            />
            
            <CommonDialog
                open={resetDialog}
                size="sm"
                padding="0px"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>Reset Password</Typography>
                            <Close
                                sx={{
                                    color: theme.palette.background.dark, fontSize: 20,
                                    float: 'right',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    setFormData({
                                        password: '',
                                        confirmpassword: ''
                                    });
                                    setShowPassword(false);
                                    setShowConfirmPassword(false);
                                    setMentorId(null);
                                    setResetDialog(false);
                                }}
                            />
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Grid container spacing={2} mt={0.1} mb={1}>
                        <Grid item md={12}>
                            <CommonFormField
                                label="Password"
                                type="password"
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                placeholder="•••••••••"
                                error={errors.password}
                                showPassword={showPassword}
                                onTogglePassword={() => setShowPassword(prev => !prev)}
                            />
                        </Grid>

                        <Grid item md={12}>
                            <CommonFormField
                                label="Confirm Password"
                                type="password"
                                name="confirmpassword"
                                value={formData.confirmpassword}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                placeholder="•••••••••"
                                error={errors.confirmpassword}
                                showPassword={showConfirmPassword}
                                onTogglePassword={() => setShowConfirmPassword(prev => !prev)}
                            />
                        </Grid>
                    </Grid>
                }
                actionButtons={(
                    <CommonButton
                        label='Update Password'
                        size="small"
                        background={theme.palette.secondary.main}
                        color={theme.palette.background.main}
                        fontWeight={700}
                        marginBottom="10px"
                        handleClick={(e) => {
                            handleSubmit(e);
                        }}
                    />
                )}
            />
            
            <CommonDialog
                open={!!editConfirm}
                size="sm"
                padding="0px"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>Edit Mentor Profile</Typography>
                            <Close
                                sx={{
                                    color: theme.palette.background.dark, fontSize: 20,
                                    float: 'right',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    setMentorData({
                                        first_name: '',
                                        last_name: '',
                                        email: '',
                                        phone_number: '',
                                        service_offers: []
                                    });
                                    setFirstNameError('');
                                    setLastNameError('');
                                    setEditConfirm(null);
                                }}
                            />
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Grid container spacing={2} mt={0.1} mb={1}>
                        <Grid item sm={6}>
                            <CommonFormField
                                label="First Name *"
                                name="first_name"
                                value={mentorData.first_name}
                                onChange={(e) => {
                                    const newValue = e.target.value;
                                    setMentorData((prev) => ({
                                        ...prev,
                                        first_name: newValue,
                                    }));
                                    validateName(newValue, 'first_name');
                                }}
                                placeholder="Enter first name"
                                error={!!firstNameError}
                                helperText={firstNameError}
                            />
                        </Grid>
                        <Grid item sm={6}>
                            <CommonFormField
                                label="Last Name *"
                                name="last_name"
                                value={mentorData.last_name}
                                onChange={(e) => {
                                    const newValue = e.target.value;
                                    setMentorData((prev) => ({
                                        ...prev,
                                        last_name: newValue,
                                    }));
                                    validateName(newValue, 'last_name');
                                }}
                                placeholder="Enter last name"
                                error={!!lastNameError}
                                helperText={lastNameError}
                            />
                        </Grid>
                        <Grid item sm={6}>
                            <CommonFormField
                                label="Email *"
                                type="email"
                                name="email"
                                value={mentorData.email}
                                onChange={(e) => {
                                    setMentorData((prev) => ({
                                        ...prev,
                                        email: e.target.value,
                                    }));
                                }}
                                placeholder="Enter email"
                            />
                        </Grid>
                        <Grid item sm={6}>
                            <CommonFormField
                                label="Phone Number"
                                name="phone"
                                value={mentorData.phone_number}
                                onChange={(e) => {
                                    setMentorData((prev) => ({
                                        ...prev,
                                        phone_number: e?.phone,
                                    }));
                                }}
                                placeholder="Enter phone number"
                                isPhone={true}
                            />
                        </Grid>
                        <Grid item sm={12}>
                            <Typography color={theme.palette.background.dark}>Services Offered *</Typography>
                            <FormGroup>
                                {serviceOptions.map((service) => (
                                    <FormControlLabel
                                        key={service.value}
                                        control={
                                            <Checkbox
                                                value={service.value}
                                                checked={mentorData.service_offers?.includes(service.value)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setMentorData((prev) => ({
                                                            ...prev,
                                                            service_offers: [...(prev.service_offers || []), e.target.value]
                                                        }));
                                                    } else {
                                                        setMentorData((prev) => ({
                                                            ...prev,
                                                            service_offers: (prev.service_offers || []).filter((a) => a !== e.target.value)
                                                        }));
                                                    }
                                                }}
                                            />
                                        }
                                        label={service.label}
                                    />
                                ))}
                            </FormGroup>
                        </Grid>
                    </Grid>
                }
                actionButtons={(
                    <CommonButton
                        label='Update'
                        size="small"
                        background={theme.palette.secondary.main}
                        color={theme.palette.background.main}
                        fontWeight={700}
                        marginBottom="10px"
                        disabled={
                            !mentorData.first_name.trim() || 
                            !!firstNameError ||
                            !mentorData.last_name.trim() || 
                            !!lastNameError ||
                            !mentorData.email.trim() ||
                            !mentorData.service_offers?.length > 0 ||
                            (mentorData.phone_number.includes(+91) ? mentorData.phone_number.length < 12 : mentorData.phone_number.length < 11)
                        }
                        handleClick={async (e) => {
                            e.preventDefault();
                            try {
                                if (validateEmail(mentorData.email)) {
                                    if (mentorData.service_offers.length > 0) {
                                        dispatch(setLoading({
                                            loading: true
                                        }));
                                        const response = await updateMentorData(editConfirm.user_id, removeEmptyFields(mentorData));
                                        if (response.success) {
                                            showNotification(response.message, 'success');
                                            dispatch(setLoading({
                                                loading: false
                                            }));
                                            setMentorData({
                                                first_name: '',
                                                last_name: '',
                                                email: '',
                                                phone_number: '',
                                                service_offers: []
                                            });
                                            getMentorData();
                                            setEditConfirm(null);
                                        }
                                    } else {
                                        showNotification('Please select service offered', 'error');
                                    }
                                } else {
                                    showNotification('Please enter a valid email address', 'error');
                                }
                            } catch (err) {
                                const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                dispatch(setLoading({
                                    loading: false
                                }));
                                showNotification(errorMessage, 'error');
                            }
                        }}
                    />
                )}
            />
            
            <CommonDialog
                open={addMentor}
                size="sm"
                padding="0px"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>Invite Mentor</Typography>
                            <Close
                                sx={{
                                    color: theme.palette.background.dark, fontSize: 20,
                                    float: 'right',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    setMentorData({
                                        first_name: '',
                                        last_name: '',
                                        email: '',
                                        phone_number: '',
                                        service_offers: []
                                    });
                                    setFirstNameError('');
                                    setLastNameError('');
                                    setAddMentor(false);
                                }}
                            />
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Grid container spacing={2} mt={0.1} mb={1}>
                        <Grid item sm={6}>
                            <CommonFormField
                                label="First Name *"
                                name="first_name"
                                value={mentorData.first_name}
                                onChange={(e) => {
                                    const newValue = e.target.value;
                                    setMentorData((prev) => ({
                                        ...prev,
                                        first_name: newValue,
                                    }));
                                    validateName(newValue, 'first_name');
                                }}
                                placeholder="Enter first name"
                                error={!!firstNameError}
                                helperText={firstNameError}
                            />
                        </Grid>
                        <Grid item sm={6}>
                            <CommonFormField
                                label="Last Name *"
                                name="last_name"
                                value={mentorData.last_name}
                                onChange={(e) => {
                                    const newValue = e.target.value;
                                    setMentorData((prev) => ({
                                        ...prev,
                                        last_name: newValue,
                                    }));
                                    validateName(newValue, 'last_name');
                                }}
                                placeholder="Enter last name"
                                error={!!lastNameError}
                                helperText={lastNameError}
                            />
                        </Grid>
                        <Grid item sm={6}>
                            <CommonFormField
                                label="Email *"
                                type="email"
                                name="email"
                                value={mentorData.email}
                                onChange={(e) => {
                                    setMentorData((prev) => ({
                                        ...prev,
                                        email: e.target.value,
                                    }));
                                }}
                                placeholder="Enter email"
                            />
                        </Grid>
                        <Grid item sm={6}>
                            <CommonFormField
                                label="Phone Number"
                                name="phone"
                                value={mentorData.phone_number}
                                onChange={(e) => {
                                    console.log(e.phone);
                                    setMentorData((prev) => ({
                                        ...prev,
                                        phone_number: e?.phone,
                                    }));
                                }}
                                placeholder="Enter phone number"
                                isPhone={true}
                            />
                        </Grid>
                        <Grid item sm={12}>
                            <Typography color={theme.palette.background.dark}>Services Offered *</Typography>
                            <FormGroup>
                                {serviceOptions.map((service) => (
                                    <FormControlLabel
                                        key={service.value}
                                        control={
                                            <Checkbox
                                                value={service.value}
                                                checked={mentorData.service_offers?.includes(service.value)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setMentorData((prev) => ({
                                                            ...prev,
                                                            service_offers: [...(prev.service_offers || []), e.target.value]
                                                        }));
                                                    } else {
                                                        setMentorData((prev) => ({
                                                            ...prev,
                                                            service_offers: (prev.service_offers || []).filter((a) => a !== e.target.value)
                                                        }));
                                                    }
                                                }}
                                            />
                                        }
                                        label={service.label}
                                    />
                                ))}
                            </FormGroup>
                        </Grid>
                    </Grid>
                }
                actionButtons={(
                    <CommonButton
                        label='Invite'
                        size="small"
                        background={theme.palette.secondary.main}
                        color={theme.palette.background.main}
                        fontWeight={700}
                        marginBottom="10px"
                        disabled={
                            !mentorData.first_name.trim() || 
                            !!firstNameError ||
                            !mentorData.last_name.trim() || 
                            !!lastNameError ||
                            !mentorData.email.trim() ||
                            !mentorData.service_offers?.length > 0 ||
                            (mentorData.phone_number.includes(+91) ? mentorData.phone_number.length < 12 : mentorData.phone_number.length < 11)
                        }
                        handleClick={async (e) => {
                            e.preventDefault();
                            try {
                                if (validateEmail(mentorData.email)) {
                                    if (mentorData.service_offers.length > 0) {
                                        dispatch(setLoading({
                                            loading: true
                                        }));
                                        const response = await inviteMentor(removeEmptyFields(mentorData));
                                        if (response.success) {
                                            showNotification(response.message, 'success');
                                            dispatch(setLoading({
                                                loading: false
                                            }));
                                            setMentorData({
                                                first_name: '',
                                                last_name: '',
                                                email: '',
                                                phone_number: '',
                                                service_offers: []
                                            });
                                            getMentorData();
                                            setAddMentor(false);
                                        }
                                    } else {
                                        showNotification('Please select service offered', 'error');
                                    }
                                } else {
                                    showNotification('Please enter a valid email address', 'error');
                                }
                            } catch (err) {
                                const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                dispatch(setLoading({
                                    loading: false
                                }));
                                showNotification(errorMessage, 'error');
                            }
                        }}
                    />
                )}
            />
            
            <Box display="flex" justifyContent="space-between">
                <Typography fontSize={20} fontWeight={600}>Mentor List</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '50%', justifyContent: 'flex-end' }}>
                    <CommonButton
                        label="Add Mentor"
                        startIcon={<Add />}
                        marginBottom="0px"
                        color="White"
                        background={theme.palette.secondary.main}
                        width="140px"
                        handleClick={() => setAddMentor(true)}
                    />
                </Box>
            </Box>
            
            <Box pt={1} pb={0}>
                <StyledTableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                                <Cell>Mentor Name</Cell>
                                <Cell>Email</Cell>
                                <Cell>Status</Cell>
                                <Cell width={'25%'}>Actions</Cell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {mentors?.data?.length > 0 ? mentors?.data?.map((user, index) => (
                                <TableRow key={index}>
                                    <InnerCell>
                                        {`${user?.user?.first_name} ${user?.user?.last_name ? user?.user?.last_name : ''}`}
                                    </InnerCell>
                                    <InnerCell style={{ color: 'black' }}>
                                        {user?.user?.email}
                                    </InnerCell>
                                    <InnerCell>
                                        {MentorStatus[user?.status]}
                                    </InnerCell>
                                    <InnerCell sx={{ display: 'flex', gap: 1 }}>
                                        <CommonButton
                                            label="Edit"
                                            marginBottom="0px"
                                            height="30px"
                                            width="70px"
                                            color="white"
                                            background={theme.palette.secondary.main}
                                            handleClick={async () => {
                                                try {
                                                    dispatch(setLoading({
                                                        loading: true
                                                    }));
                                                    const response = await getMentor(user?.user_id);
                                                    setMentorData({
                                                        first_name: user?.user?.first_name,
                                                        last_name: user?.user?.last_name,
                                                        email: user?.user?.email,
                                                        phone_number: user?.user?.phone_number,
                                                        service_offers: response?.data?.mentorProfile?.service_offers
                                                    });
                                                    setFirstNameError('');
                                                    setLastNameError('');
                                                    setEditConfirm(user);
                                                    dispatch(setLoading({
                                                        loading: false
                                                    }));
                                                } catch (err) {
                                                    const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                                    dispatch(setLoading({
                                                        loading: false
                                                    }));
                                                    showNotification(errorMessage, 'error');
                                                }
                                            }}
                                        />
                                        {user?.status !== 'INVITESENT' ? (
                                            <CommonButton
                                                label={user?.status === 'ACTIVE' ? 'Block' : 'Unblock'}
                                                marginBottom="0px"
                                                height="30px"
                                                width="70px"
                                                color="white"
                                                background={user?.status === 'ACTIVE' ? "red" : "green"}
                                                handleClick={() => {
                                                    if (user?.status === 'ACTIVE') {
                                                        setDeleteConfirm({ ...user, action: 'block' })
                                                    } else {
                                                        handleBlock(user);
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <CommonButton
                                                label="Resend Invitation"
                                                marginBottom="0px"
                                                height="30px"
                                                width="150px"
                                                color="white"
                                                background={theme.palette.primary.success}
                                                handleClick={async () => {
                                                    try {
                                                        dispatch(setLoading({
                                                            loading: true
                                                        }));
                                                        const response = await resendInvitation(user?.mentor_profile_id);
                                                        if (response.success) {
                                                            showNotification(response.message, 'success');
                                                            dispatch(setLoading({
                                                                loading: false
                                                            }));
                                                            getMentorData();
                                                        }
                                                    } catch (err) {
                                                        const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                                        dispatch(setLoading({
                                                            loading: false
                                                        }));
                                                        showNotification(errorMessage, 'error');
                                                    }
                                                }}
                                            />
                                        )}
                                        {user?.status !== 'INVITESENT' && (
                                            <CommonButton
                                                label="Reset Password"
                                                marginBottom="0px"
                                                height="30px"
                                                width="150px"
                                                color="white"
                                                background={theme.palette.secondary.main}
                                                handleClick={() => {
                                                    setResetDialog(true);
                                                    setMentorId(user?.user_id);
                                                }}
                                            />
                                        )}
                                        {/* BUG - 5: Delete Button */}
                                        <CommonButton
                                            label="Delete"
                                            marginBottom="0px"
                                            height="30px"
                                            width="70px"
                                            color="white"
                                            background="red"
                                            handleClick={() => {
                                                setDeleteConfirm({ ...user, action: 'delete' })
                                            }}
                                        />
                                    </InnerCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        <Typography>No Mentors Found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </StyledTableContainer>
            </Box>

            {/* Pagination Component */}
            <Box sx={{ borderTop: `1px solid ${theme.palette.grey[300]}` }}>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 15, 20, 25, 50]}
                    component="div"
                    count={mentors?.pagination?.total}
                    rowsPerPage={pageSize}
                    page={pageNo}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                            fontSize: '0.875rem',
                        },
                    }}
                />
            </Box>
        </Fragment>
    )
}

export default ManageMentor;